import React from "react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { cn } from "@util/tailwind";
import { useOuraMerchantButton } from "@oura/role";
// import { useSettingsSnackbar } from "@/polymet/components/settings-snackbar-messages";

interface GoogleSSOPasswordDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function GoogleSSOPasswordDialog({
    open,
    onOpenChange,
}: GoogleSSOPasswordDialogProps) {
    // Oura merchants get pill buttons; every other product keeps the shared shape.
    const pill = useOuraMerchantButton();
    const [showNewPassword, setShowNewPassword] = React.useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
    const [newPassword, setNewPassword] = React.useState("");
    const [confirmPassword, setConfirmPassword] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    // const snackbar = useSettingsSnackbar();

    const handleChangePassword = async () => {
        // Validation
        if (!newPassword.trim()) {
            // snackbar.validationError("Please enter a new password.");
            return;
        }

        if (newPassword.length < 8) {
            // snackbar.weakPasswordError();
            return;
        }

        if (newPassword !== confirmPassword) {
            // snackbar.passwordMismatchError();
            return;
        }

        setIsLoading(true);

        try {
            // Simulate API call for Google SSO password change
            await new Promise((resolve, reject) => {
                setTimeout(() => {
                    // Simulate random success/failure for demo
                    if (Math.random() > 0.1) {
                        resolve(true);
                    } else {
                        reject(new Error("Failed to update password"));
                    }
                }, 1500);
            });

            console.log("Google SSO password changed successfully");
            // snackbar.passwordChangedSuccess();

            // Reset form and close dialog
            setNewPassword("");
            setConfirmPassword("");
            onOpenChange(false);
        } catch (error) {
            // snackbar.passwordChangeError();
            console.error("Error changing Google SSO password:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        // Reset form and close dialog
        setNewPassword("");
        setConfirmPassword("");
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Set New Password</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Google SSO Info Banner */}
                    <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm font-semibold">G</span>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-blue-900">
                                Google SSO Account
                            </p>
                            <p className="text-xs text-blue-700">
                                Your account is secured by Google authentication.
                            </p>
                        </div>
                    </div>

                    {/* New Password */}
                    <div className="space-y-2">
                        <Label
                            htmlFor="new-password"
                            className="text-sm font-medium text-gray-900"
                        >
                            New Password
                        </Label>
                        <div className="relative">
                            <Input
                                id="new-password"
                                type={showNewPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="pr-10"
                                placeholder="Enter your new password"
                            />

                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                            >
                                {showNewPassword ? (
                                    <EyeOffIcon className="h-4 w-4 text-gray-400" />
                                ) : (
                                    <EyeIcon className="h-4 w-4 text-gray-400" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Confirm New Password */}
                    <div className="space-y-2">
                        <Label
                            htmlFor="confirm-password"
                            className="text-sm font-medium text-gray-900"
                        >
                            Confirm New Password
                        </Label>
                        <div className="relative">
                            <Input
                                id="confirm-password"
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="pr-10"
                                placeholder="Confirm your new password"
                            />

                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? (
                                    <EyeOffIcon className="h-4 w-4 text-gray-400" />
                                ) : (
                                    <EyeIcon className="h-4 w-4 text-gray-400" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Password Requirements */}
                    <div className="text-xs text-gray-500 space-y-1">
                        <p className="font-medium">Password requirements:</p>
                        <ul className="list-disc list-inside space-y-0.5 ml-2">
                            <li>At least 8 characters long</li>
                            <li>Include uppercase and lowercase letters</li>
                            <li>Include at least one number</li>
                        </ul>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" className={pill} onClick={handleCancel} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleChangePassword}
                        className={cn("bg-blue-600 hover:bg-blue-700 text-white", pill)}
                        disabled={isLoading}
                    >
                        {isLoading ? "Setting Password..." : "Set New Password"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
